import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useGetErrorMessage } from '@/utils/dbMessagesHelper';
import { toast } from 'sonner';

import { email_template_config } from '@/config/email_template.config';
import useForgotPassword from '@/hooks/useForgotPassword';
import useSessionStorageInfo from '@/hooks/useSessionStorageInfo';
import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import InputErrorMsg from '@/components/reUsables/InputErrorMsg';

const PasswordForm = ({
  email,
  loading,
  setIsEmailForm,
  password,
  setPassword,
  handleLogin,
  tenantId,
  errorMsg,
}: {
  email: string;
  loading: boolean;
  password: string;
  setPassword: (password: string) => void;
  setIsEmailForm: (isEmailForm: boolean) => void;
  handleLogin: (e: React.FormEvent<HTMLFormElement>) => void;
  tenantId: string;
  errorMsg?: string;
}) => {
  const { translations, isLoading: isTranslationsLoading } =
    useTranslations('auth_page');

  const { getDBMessage } = useGetErrorMessage();
  const passwordLabel = translations?.password || 'Password';
  const password_placeholder =
    translations?.password_placeholder || 'Enter password';
  const forgot_password = translations?.forgot_password || 'Forgot Password?';
  const sending = translations?.sending || 'Sending...';
  const login_with_another_account =
    translations?.login_with_another_account || 'Login with another account?';
  const login = translations?.login || 'Login';

  const router = useRouter();
  const { clientId, redirecturl, postParent } = useSessionStorageInfo();

  const {
    loading: forgotPasswordLoading,
    data,
    handleClick,
    error,
  } = useForgotPassword(email_template_config.RESET_PASSWORD_TEMPLATE);

  const handleForgotPassword = () => {
    handleClick(email, tenantId, clientId, redirecturl, postParent);
  };

  useEffect(() => {
    if (error) {
      toast.error(getDBMessage(error) || error);
    }
  }, [error]);

  useEffect(() => {
    if (data?.success) {
      toast.success(getDBMessage(data?.message) || data?.message);
      router.push(`/forgot-password?email=${email}&tenantId=${tenantId}`);
    }
  }, [data]);

  if (isTranslationsLoading) {
    return (
      <div className="mt-8 space-y-4">
        <div className="space-y-2">
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mt-8">
        <form onSubmit={handleLogin}>
          <Label htmlFor="password">{passwordLabel}</Label>
          <Input
            className={`mt-2 ${errorMsg && 'border-red-500'}`}
            type="password"
            id="password"
            required
            placeholder={password_placeholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          {errorMsg && (
            <div className="my-1">
              <InputErrorMsg text={errorMsg} />
            </div>
          )}
          <p
            onClick={
              forgotPasswordLoading
                ? () => console.log('Loading...')
                : handleForgotPassword
            }
            className={`mt-4 text-sm font-medium ${forgotPasswordLoading ? 'cursor-wait text-zinc-400' : 'cursor-pointer text-primary-950'} `}
          >
            {forgotPasswordLoading ? sending : forgot_password}
          </p>
          <br />
          <Button disabled={loading} type="submit" size="fullWidth">
            {loading ? 'Loading...' : login}
          </Button>
        </form>
      </div>
      <p
        onClick={() => setIsEmailForm(true)}
        className="cursor-pointer pt-6 text-center text-sm font-medium text-primary-950 hover:underline"
      >
        {login_with_another_account}
      </p>
    </div>
  );
};

export default PasswordForm;
